import { DataTableStatisticCSVItem, StatisticDtoScore } from "components/StatisticsDataTable/Dto/DataTableStatisticDto";
import { MUIDataTableColumn } from "mui-datatables";
import type { TFunction } from "i18next";
import { LessonProgressStatus } from "models/LessonProgressStatus";
import { isUncompleted } from "helpers/lessonProgress";
import { datePart } from "components/StatisticsDataTable/Columns/DateCompletedColumn";
import { buildExportFileName } from "helpers/exportFileName";

type RowShape = { data: unknown[] | DataTableStatisticCSVItem };

const esc = (v: unknown, sep: string) => {
    const s = v == null ? "" : String(v);
    return /["\n\r]/.test(s) || s.includes(sep)
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

const toRowArray = (
    row: { data: unknown[] | DataTableStatisticCSVItem },
  ): unknown[] => {
    if (Array.isArray(row.data)) {
        return row.data;
    }
    const o = row.data as DataTableStatisticCSVItem;
    return [
      o.userDisplayName,
      o.lessonName,
      o.userTagA,
      o.userTagB,
      o.dateCompleted,
      o.rewardType,
      o.timeSpent,
      o.score,
    ];
  };

export const triggerBlobDownload = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export const triggerCsvDownload = (csv: string, filename: string): void => {
    triggerBlobDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
};

export const generateFileName = (prefix: string): string => buildExportFileName(prefix);

const formatCell = (
  t: TFunction,
  columnName: string | undefined,
  value: unknown,
): string => {
  if (columnName === 'dateStarted') {
    return datePart(typeof value === 'string' ? value : '');
  }
  if (columnName === 'lessonProgressStatus') {
    const status = typeof value === 'number' ? (value as LessonProgressStatus) : null;
    if (status === null) return '';
    return isUncompleted(status) ? t('statistics.notCompleted') : t('statistics.completed');
  }
  if (columnName === 'passed') {
    return value ? t('general.yes') : t('general.no');
  }
  return value == null ? '' : String(value);
};

export const buildStatisticsCsv = (
  t: TFunction,
  columns: MUIDataTableColumn[],
  data: Array<RowShape>,
  opts?: { separator?: string; pointsColumn?: string }
): string => {
  const SEP = opts?.separator ?? ";";
  const pointsColumn = opts?.pointsColumn ?? "score";
  const idxPoints = columns.findIndex((c) => c?.name === pointsColumn);

  const headerCells: string[] = [];
  columns.forEach((col, i) => {
    if (i === idxPoints) {
      headerCells.push(t('statistics.earnedPoints'), t('statistics.maxPoints'));
    } else {
      headerCells.push(String(col?.label ?? col?.name ?? ""));
    }
  });
  const header = headerCells.map((h) => esc(h, SEP)).join(SEP);

  const rows = data
    .map((row) => {
      const arr = toRowArray(row);
      const cells: string[] = [];
      const pointsObj = (arr[idxPoints] ?? {}) as Partial<StatisticDtoScore>;

      columns.forEach((col, i) => {
        if (i === idxPoints) {
          const scored = pointsObj.scoredPoints ?? "";
          const max = pointsObj.maxPoints ?? "";
          cells.push(esc(scored, SEP), esc(max, SEP));
        } else {
          cells.push(esc(formatCell(t, col?.name, arr[i]), SEP));
        }
      });

      return cells.join(SEP);
    })
    .join("\n");

  return `\uFEFF${header}\n${rows}`;
};
